import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import TalentScoutingDashboard from "../components/scouting/TalentScoutingDashboard";
import ContactRequestModal from "../components/scouting/ContactRequestModal";
import { useAuth } from "../context/AuthContext";
import { useNotifications } from "../components/layout/routing/AppShell";
import { useScoutContactRequestsMap } from "../hooks/queries/useContactRequests";
import {
  deriveTalentFunnel,
  deriveTopSports,
} from "../utils/scoutDashboardMetrics";
import {
  getScoutContactAction,
  scoutThreadPath,
} from "../utils/scoutContactAction";

const getAthleteId = (player) => player?.id ?? player?.athlete_id;

const ScoutDashboardRoute = () => {
  const navigate = useNavigate();
  const {
    scoutData,
    scoutTrackingData,
    linkedAthletes,
    availableAthletes,
    activeSearchId,
  } = useAuth();
  const { openNotifications } = useNotifications();
  const [contactAthlete, setContactAthlete] = useState(null);
  // Stable: the modal's focus-trap effect re-runs (and refocuses the first
  // field) whenever onClose changes identity.
  const closeContactModal = useCallback(() => setContactAthlete(null), []);

  // Solicitudes de contacto del scout: alimentan el "Contactados" del embudo.
  // El backend ya las filtra por el scout autenticado, así que no lleva id.
  const { data: contactRequests = {} } = useScoutContactRequestsMap({
    enabled: Boolean(scoutData?.id),
  });

  // El panel se alimenta de datos vivos, no del blob `scout_tracking.metrics`
  // (que nadie escribe y dejaba el INICIO en 0 / 0 / 0 para todo scout nuevo).
  const liveMetrics = useMemo(
    () => ({
      talent_funnel: deriveTalentFunnel(linkedAthletes, contactRequests),
      top_sports: deriveTopSports(linkedAthletes),
    }),
    [linkedAthletes, contactRequests],
  );

  // "Contactar" acts right here on Home: open the request form, or jump to the
  // chat once the athlete approved. The card itself still opens the profile.
  const handleContactAthlete = (player) => {
    const request = contactRequests[getAthleteId(player)];
    const action = getScoutContactAction(request);
    if (action.kind === "message") navigate(scoutThreadPath(request));
    else if (action.kind === "request") setContactAthlete(player);
  };

  return (
    <>
      <TalentScoutingDashboard
        scoutProfile={scoutData}
        trackingData={scoutTrackingData}
        metrics={liveMetrics}
        suggestedAthletes={availableAthletes}
        contactRequestsByAthleteId={contactRequests}
        onNotificationsClick={openNotifications}
        onSearchClick={() =>
          navigate(
            activeSearchId
              ? `/scout/searches/${activeSearchId}`
              : "/scout/searches",
          )
        }
        onOpenProfile={() => navigate("/scout/profile")}
        onContactAthlete={handleContactAthlete}
        onOpenAthlete={(player) => {
          const athleteId = getAthleteId(player);
          navigate(
            athleteId ? `/scout/athletes/${athleteId}` : "/scout/athletes",
          );
        }}
      />
      <ContactRequestModal
        isOpen={Boolean(contactAthlete)}
        onClose={closeContactModal}
        athlete={contactAthlete}
      />
    </>
  );
};

export default ScoutDashboardRoute;
